// ═══════════════════════════════════════════════════════════════════════════
// SOCIAL SHARING UTILITIES
// Generate shareable tweets for achievements, milestones, and stats
// ═══════════════════════════════════════════════════════════════════════════

// Base URLs and handles
const TWITTER_INTENT_URL = 'https://twitter.com/intent/tweet';
const DISTIL_HANDLE = '@DistilProtocol';
const SUI_HANDLE = '@SuiNetwork';
const DISTIL_URL = 'distil.xyz';

// Hashtags
const HASHTAGS = {
  sui: '#SuiNetwork',
  defi: '#DeFi',
  cleanWallet: '#CleanWallet',
  milestone: '#Milestone',
  achievement: '#Achievement',
};

/**
 * Generate tweet for tier upgrade
 */
export function generateTierUpgradeTweet({ tier, purityScore, purifications, bonusPercentage }) {
  const tierEmojis = {
    'Bronze': '🥉',
    'Silver': '🥈',
    'Gold': '🏆',
    'Diamond': '💎'
  };
  
  const emoji = tierEmojis[tier] || '⭐';
  
  return `Just reached ${tier} Tier in ${DISTIL_HANDLE}! ${emoji}

💎 ${purityScore.toLocaleString()} purity score
🧹 ${purifications.toLocaleString()} objects cleaned
🔥 Earning +${bonusPercentage}% bonus rewards

Cleaning up ${HASHTAGS.sui} one scam at a time!

${DISTIL_URL}`;
}

/**
 * Generate tweet for milestone achievement
 */
export function generateMilestoneTweet({ phase, phaseName, newRate, totalEmitted }) {
  return `🎉 The ${DISTIL_HANDLE} community just unlocked ${phaseName}!

📉 New emission rate: ${newRate} $DISTIL
🌍 ${(totalEmitted / 1_000_000).toFixed(1)}M tokens distributed
💪 Building the cleanest blockchain together

${HASHTAGS.sui} ${HASHTAGS.defi} ${HASHTAGS.milestone}`;
}

/**
 * Generate tweet for personal stats
 */
export function generateStatsTweet({ tier, purityScore, purifications, distilEarned, percentile }) {
  const tierEmojis = {
    'Bronze': '🥉',
    'Silver': '🥈',
    'Gold': '🏆',
    'Diamond': '💎',
    'Mythic': '👑'
  };
  
  const emoji = tierEmojis[tier] || '⭐';
  
  let rankText = '';
  if (percentile) {
    rankText = `📊 Top ${percentile}% of purifiers\n`;
  }
  
  return `My ${DISTIL_HANDLE} cleanup stats 🧹

${emoji} ${tier} Tier holder
🗑️ ${purifications.toLocaleString()} spam purified
🔥 ${distilEarned.toLocaleString()} $DISTIL earned
${rankText}
Cleaning ${HASHTAGS.sui} one scam at a time!

${DISTIL_URL}`;
}

/**
 * Generate tweet for purification milestone (100, 500, 1000, etc)
 */
export function generatePurificationMilestoneTweet({ purifications, purityScore, tier }) {
  const milestones = {
    100: { emoji: '💯', message: 'First 100 purifications!' },
    500: { emoji: '🔥', message: '500 purifications strong!' },
    1000: { emoji: '🚀', message: '1K purifications achieved!' },
    5000: { emoji: '💎', message: '5K purifications! Unstoppable!' },
    10000: { emoji: '👑', message: '10K purifications! LEGENDARY!' },
  };
  
  const milestone = milestones[purifications] || { emoji: '✨', message: `${purifications.toLocaleString()} purifications!` };
  
  return `${milestone.emoji} ${milestone.message}

Just hit ${purifications.toLocaleString()} spam objects purified on ${DISTIL_HANDLE}!

💎 ${purityScore.toLocaleString()} purity score
🏆 ${tier} Tier

Join the cleanup revolution: ${DISTIL_URL}

${HASHTAGS.sui} ${HASHTAGS.cleanWallet}`;
}

/**
 * Generate tweet for holding tier achievement
 */
export function generateHoldingTierTweet({ tier, distilBalance, bonusPercentage, benefits }) {
  return `Just unlocked ${tier} holding tier in ${DISTIL_HANDLE}! 💎

💰 ${distilBalance.toLocaleString()} $DISTIL held
⚡ +${bonusPercentage}% rewards on every purge
${benefits ? `🎁 ${benefits}` : ''}

Hold more, earn more! 📈

${DISTIL_URL} ${HASHTAGS.defi}`;
}

/**
 * Generate tweet for burn milestone
 */
export function generateBurnMilestoneTweet({ totalBurned, burnPercentage }) {
  return `🔥 ${DISTIL_HANDLE} has permanently burned ${totalBurned.toLocaleString()} $DISTIL!

📉 ${burnPercentage}% of supply removed forever
💎 Making $DISTIL scarcer with every purge

Deflationary tokenomics in action! 🚀

${DISTIL_URL} ${HASHTAGS.defi}`;
}

/**
 * Generate tweet for general achievement
 */
export function generateGeneralTweet({ message, stats = [] }) {
  const statsText = stats.length > 0 
    ? '\n' + stats.map(s => `${s.emoji} ${s.text}`).join('\n')
    : '';
  
  return `${message}${statsText}

${DISTIL_HANDLE} - Cleaning ${HASHTAGS.sui} one scam at a time!

${DISTIL_URL}`;
}

/**
 * Open Twitter share dialog
 */
export function shareOnTwitter(tweetText) {
  const url = `${TWITTER_INTENT_URL}?text=${encodeURIComponent(tweetText)}`;
  
  // Open in new window
  const width = 550;
  const height = 420;
  const left = (window.screen.width - width) / 2;
  const top = (window.screen.height - height) / 2;
  
  window.open(
    url,
    'twitter-share',
    `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0`
  );
}

/**
 * Check if milestone purification count
 */
export function isPurificationMilestone(count) {
  const milestones = [100, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000];
  return milestones.includes(count);
}

/**
 * Check if burn milestone
 */
export function isBurnMilestone(burnAmount) {
  const milestones = [1000, 5000, 10000, 25000, 50000, 100000];
  return milestones.includes(Math.floor(burnAmount));
}

// Export all functions
export default {
  generateTierUpgradeTweet,
  generateMilestoneTweet,
  generateStatsTweet,
  generatePurificationMilestoneTweet,
  generateHoldingTierTweet,
  generateBurnMilestoneTweet,
  generateGeneralTweet,
  shareOnTwitter,
  isPurificationMilestone,
  isBurnMilestone,
};
